import {
  AlertFormRef,
  Button,
  ColumnData,
  EditAlertForm,
  Icon,
  SlideAction,
  tokens,
  useSlidePanel,
} from '@gripp/shared-ui';
import { MutableRefObject, ReactNode, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, StyleSheet, View } from 'react-native';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

/** Wiring the chrome hands to whichever alert form fills the column. */
export type AlertFormSlot = {
  formRef: MutableRefObject<AlertFormRef>;
  renderBottomComponent: (onSubmit: () => void, disabled: boolean) => ReactNode;
  onRegisterBackGuard: (
    guard: (onProceed: () => void, onCancel?: () => void) => void
  ) => void;
};

export type AlertFormColumnOptions = {
  /** `alerts.editAlert` or `alerts.newAlert`. */
  title: string;
  /** Back, save, and delete all land here. */
  onBack: () => void;
  renderForm: (slot: AlertFormSlot) => ReactNode;
  /** Merged over the column — transitions, debug metadata. */
  column?: Partial<ColumnData>;
};

/**
 * The alert form's SlideLayout column: reset in the header, Confirm at the
 * bottom, and the unsaved-changes back guard bound to the form.
 *
 * The asset, product, group, and calendar entry points differ only in which
 * form fills the column and where back goes, so they pass `renderForm` and
 * `onBack` and share everything else.
 */
export const useAlertFormColumn = ({
  title,
  onBack,
  renderForm,
  column,
}: AlertFormColumnOptions): ColumnData => {
  const { t } = useTranslation();
  const formRef = useRef<AlertFormRef>(null);
  const backGuardRef = useRef<
    ((onProceed: () => void, onCancel?: () => void) => void) | null
  >(null);

  return useMemo(
    () => ({
      title,
      showBackButton: true,
      onBackButtonPress: onBack,
      backButtonGuard: () =>
        new Promise<boolean>((resolve) => {
          if (!backGuardRef.current) return resolve(true);
          backGuardRef.current(
            () => resolve(true),
            () => resolve(false)
          );
        }),
      disableScroll: true, // Form scrolls itself
      headerComponent: (
        <Pressable
          style={styles.reset}
          onPress={() => formRef.current?.reset()}
          accessibilityLabel={t('versionCheck.refreshButton')}
        >
          <Icon
            name="Reset"
            size={20}
            color={tokens.colors.brand.black}
          />
        </Pressable>
      ),
      mainComponent: (
        <View style={styles.body}>
          {renderForm({
            formRef,
            onRegisterBackGuard: (guard) => {
              backGuardRef.current = guard;
            },
            renderBottomComponent: (onSubmit, disabled) => (
              <Button
                variant="rectangular"
                size="large"
                state={disabled ? 'default' : 'active'}
                disabled={disabled}
                onPress={() => {
                  if (!disabled) onSubmit();
                }}
                style={styles.confirm}
              >
                {t('alerts.confirm')}
              </Button>
            ),
          })}
        </View>
      ),
      ...column,
    }),
    [title, onBack, renderForm, column, t]
  );
};

type AlertFormPanelProps = {
  /** Where back / save / delete return to. */
  backTo: string;
  /** Carry the current query string onto `backTo` (e.g. Activity's `?view=`). */
  preserveSearch?: boolean;
};

/**
 * The edit-alert column as a route element: registers itself as the right
 * column of the shell above, which keeps its own left column — so the calendar
 * stays put while this slides in. Mount under any prefix supplying
 * `:routineId/:alertId`, the way `assetSubRoutes` mounts one entry under both
 * `/assets` and `/activities`.
 *
 * `EditAlertForm` reads the owning asset / product / group off the workflow, so
 * a caller holding only the alert can mount this as-is.
 */
export const AlertFormPanel = ({
  backTo,
  preserveSearch,
}: AlertFormPanelProps) => {
  const { routineId = '', alertId = '' } = useParams();
  const navigate = useNavigate();
  const { search } = useLocation();

  const onBack = useMemo(
    () => () => navigate(preserveSearch ? `${backTo}${search}` : backTo),
    [navigate, backTo, preserveSearch, search]
  );

  const renderForm = useMemo(
    () =>
      ({ formRef, renderBottomComponent, onRegisterBackGuard }: AlertFormSlot) => (
        <EditAlertForm
          key={alertId}
          alertId={alertId}
          routineId={routineId}
          onClose={onBack}
          formRef={formRef}
          renderBottomComponent={renderBottomComponent}
          onRegisterBackGuard={onRegisterBackGuard}
        />
      ),
    [alertId, routineId, onBack]
  );

  const column = useAlertFormColumn({
    title: 'alerts.editAlert',
    onBack,
    renderForm,
    // Back collapses to the shell's own left column rather than handing off to
    // another page, so SINGLE (left expands to full width) is the transition —
    // and it has to be declared: SlideLayout only falls back to it when there
    // is no `onBackButtonPress`, which this column has.
    column: { backButtonTransition: SlideAction.SINGLE },
  });

  const registered = routineId && alertId ? column : null;
  useSlidePanel('right', registered, [registered]);

  return null;
};

const styles = StyleSheet.create({
  body: { flex: 1 },
  reset: { padding: tokens.spacing.xs },
  confirm: { width: '100%' },
});
